import { isApiClientError } from '@ps/api-client';
import { create } from 'zustand';
import { createAuthApi } from './api.js';
/**
 * The authentication state each app binds its screens to.
 *
 * The apps differ in their routes and their copy, not in what it means to be
 * signed in — so that part lives here and is shared.
 */
export function createAuthStore({ client, onSignedOut }) {
    const api = createAuthApi(client);
    let restoring = null;
    return create((set) => ({
        status: 'unknown',
        user: null,
        profileComplete: false,
        restore: async () => {
            // One check at a time: several screens mounting together must not each
            // fire their own /auth/me.
            restoring ??= (async () => {
                try {
                    const me = await api.me();
                    const { profileComplete, ...user } = me;
                    set({ status: 'authenticated', user, profileComplete });
                }
                catch (error) {
                    // A refused or missing session means anonymous. Anything else — a
                    // flat network, a 500 — must not sign the person out.
                    const refused = isApiClientError(error) && (error.isUnauthenticated || error.isForbidden);
                    if (refused) {
                        client.clearSession();
                        set({ status: 'anonymous', user: null, profileComplete: false });
                    }
                    else {
                        set({ status: 'unknown' });
                    }
                }
                finally {
                    restoring = null;
                }
            })();
            return restoring;
        },
        signIn: ({ user, tokens, profileComplete }) => {
            client.setTokens(tokens);
            set({ status: 'authenticated', user, profileComplete });
        },
        saveProfile: async (input) => {
            const user = await api.saveProfile(input);
            set({ user, profileComplete: Boolean(user.displayName) });
        },
        signOut: async () => {
            try {
                await api.logout();
            }
            catch {
                // The session may already be gone; the local state is cleared either way.
            }
            client.clearSession();
            set({ status: 'anonymous', user: null, profileComplete: false });
            onSignedOut?.();
        },
        refresh: async () => {
            const me = await api.me();
            const { profileComplete, ...user } = me;
            set({ status: 'authenticated', user, profileComplete });
        },
    }));
}
//# sourceMappingURL=store.js.map